import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { StarIcon } from "lucide-react";
import { useState } from "react";

interface FeaturedUpgradeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onUpgrade: (featureOption: "1d" | "7d" | "30d") => void;
  propertyTitle?: string;
  isUpgrading?: boolean;
}

const featuredOptions: {
  value: "1d" | "7d" | "30d";
  label: string;
  price: string;
  description: string;
}[] = [
  {
    value: "1d",
    label: "1 Day",
    price: "20 Points",
    description: "Featured for 24 hours",
  },
  {
    value: "7d",
    label: "7 Days",
    price: "100 Points",
    description: "Featured for a full week",
  },
  {
    value: "30d",
    label: "30 Days",
    price: "300 Points",
    description: "Maximum visibility for a month",
  },
];

export function FeaturedUpgradeDialog({
  open,
  onOpenChange,
  onUpgrade,
  propertyTitle,
  isUpgrading,
}: FeaturedUpgradeDialogProps) {
  const [selectedOption, setSelectedOption] = useState<
    "1d" | "7d" | "30d" | null
  >(null);

  const handleUpgradeClick = () => {
    if (selectedOption !== null) {
      onUpgrade(selectedOption);
    }
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      setSelectedOption(null);
    }
    onOpenChange(isOpen);
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader className="space-y-3">
          <DialogTitle className="text-center flex flex-col items-center gap-2">
            <div className="bg-amber-100 p-3 rounded-full">
              <StarIcon className="h-6 w-6 text-amber-600 stroke-2" />
            </div>
            Upgrade to Featured
          </DialogTitle>
          <DialogDescription className="text-center">
            {propertyTitle
              ? `Feature "${propertyTitle}" to increase visibility and attract more potential clients.`
              : "Feature your property to increase visibility and attract more potential clients."}
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-2">
          {featuredOptions.map((option, index) => (
            <div
              key={option.value}
              className={`relative flex items-center justify-between p-4 rounded-lg border transition-all cursor-pointer ${
                selectedOption === option.value
                  ? "border-propertylk-blue bg-propertylk-tag"
                  : "border-gray-200 hover:border-propertylk-blue/50"
              }`}
              onClick={() => setSelectedOption(option.value)}
            >
              <div className="flex items-center gap-3">
                <div
                  className={`w-5 h-5 rounded-full flex items-center justify-center border ${
                    selectedOption === option.value
                      ? "border-propertylk-blue"
                      : "border-gray-300"
                  }`}
                >
                  {selectedOption === option.value && (
                    <div className="w-3 h-3 rounded-full bg-propertylk-blue" />
                  )}
                </div>
                <div>
                  <h4 className="font-medium text-gray-900">{option.label}</h4>
                  <p className="text-sm text-gray-500">{option.description}</p>
                </div>
              </div>
              <span className="font-bold text-propertylk-darkblue">
                {option.price}
              </span>
            </div>
          ))}
        </div>

        <DialogFooter className="flex justify-end gap-2 sm:justify-end">
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isUpgrading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleUpgradeClick}
            disabled={selectedOption === null || isUpgrading}
          >
            {isUpgrading ? "Upgrading..." : "Upgrade Now"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
